/**
 * Shrink a photo before it leaves the phone.
 *
 * A modern phone camera produces a 3-5 MB image; the server downscales it to 1600px and
 * throws the rest away anyway. Sending the original means waiting on an upload that is
 * ten times bigger than it needs to be, over the connection least able to afford it —
 * which is exactly the situation this app is for: someone standing in a restaurant,
 * photographing a receipt, on one bar of signal.
 *
 * Doing it here is a saving, never a correctness step. The server re-encodes and enforces
 * its own limits regardless, so a browser that cannot do this (or fails at it) just sends
 * the original and everything still works.
 */

/** Matches `MAX_LONG_EDGE` in `services/ai/images.py`; going smaller would lose detail. */
export const MAX_LONG_EDGE = 1600;
const JPEG_QUALITY = 0.85;

/** Below this there is nothing to gain, and re-encoding would only lose detail. */
const WORTH_SHRINKING_BYTES = 400_000;

async function loadBitmap(file: File): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap !== "function") return null;
  try {
    // `imageOrientation` makes the browser apply the EXIF rotation, so a receipt shot in
    // portrait does not arrive on its side.
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return null;
  }
}

export async function downscale(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  if (file.size <= WORTH_SHRINKING_BYTES) return file;

  const bitmap = await loadBitmap(file);
  if (!bitmap) return file;

  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > MAX_LONG_EDGE ? MAX_LONG_EDGE / longest : 1;
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    // If the "shrunk" version is not actually smaller, keep the original.
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], `${name}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  } finally {
    bitmap.close?.();
  }
}

/** Downscale several at once, keeping any that fail as they are. */
export async function downscaleAll(files: File[]): Promise<File[]> {
  return Promise.all(files.map((file) => downscale(file).catch(() => file)));
}
